import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Menu, 
  X, 
  ChevronDown,
  MapPin,
  Calendar,
  Users,
  Search,
  ShoppingBag,
  GraduationCap,
  Award,
  Crown,
  Map as MapIcon,
  Plane
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const CaddieHat = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 16s0-4 8-4 8 4 8 4" />
    <path d="M2 16h20" />
    <ellipse cx="12" cy="12" rx="6" ry="4" />
    <path d="M6 12c0-3.3 2.7-6 6-6s6 2.7 6 6" />
  </svg>
);

const MainLayout = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isLanding = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/explore', label: 'The Clubhouse', icon: Search },
    { href: '/calendar', label: 'The Itinerary', icon: Calendar },
    { href: '/course-map', label: 'The Local 18', icon: MapIcon },
    { href: '/marketplace', label: 'The Pro Shop', icon: ShoppingBag },
    { href: '/travel-guides', label: 'Away Games', icon: Plane },
    { href: '/pros', label: 'Directory', icon: Award },
    { href: '/hbcu', label: 'NextGen', icon: GraduationCap },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || !isLanding
            ? 'bg-deep-green shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="container-padding">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img 
                src="/logo.png" 
                alt="Fairway Alliance"
                className="h-12 md:h-14 w-auto"
              />
              <div className="hidden sm:block">
                <h1 className="text-white font-display font-bold text-lg md:text-xl leading-tight">
                  Fairway Alliance
                </h1>
                <p className="text-cream/70 text-xs tracking-wider">ONE GAME. ONE NETWORK.</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-white/90 hover:text-gold transition-colors font-body text-sm tracking-wide uppercase"
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Auth Buttons */}
            <div className="hidden md:flex items-center gap-4">
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="text-white hover:text-gold hover:bg-white/10 gap-2"
                      data-testid="user-menu-trigger"
                    >
                      <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
                        <Users className="w-4 h-4 text-gold" />
                      </div>
                      <span className="font-body">{user?.display_name || 'Member'}</span>
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" data-testid="nav-dashboard">Dashboard</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/profile" data-testid="nav-profile">Profile</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/rounds" data-testid="nav-rounds">My Rounds</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/caddie" data-testid="nav-caddie">Caddie</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={logout}
                      className="text-destructive"
                      data-testid="logout-btn"
                    >
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Link to="/login">
                    <Button 
                      variant="ghost" 
                      className="text-white hover:text-gold hover:bg-white/10"
                      data-testid="login-btn"
                    >
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button 
                      className="btn-gold"
                      data-testid="register-btn"
                    >
                      Join Now
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden text-white p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-deep-green border-t border-white/10">
            <div className="container-padding py-4 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="flex items-center gap-3 text-white/90 hover:text-gold py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 border-t border-white/10 space-y-2">
                {isAuthenticated ? (
                  <>
                    <Link 
                      to="/dashboard" 
                      className="block text-white py-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <button 
                      onClick={() => { logout(); setMobileMenuOpen(false); }}
                      className="text-gold py-2"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full border-white/30 text-white">
                        Sign In
                      </Button>
                    </Link>
                    <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full btn-gold">
                        Join Now
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-fairway text-white">
        <div className="container-padding py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <img 
                  src="/logo.png" 
                  alt="Fairway Alliance"
                  className="h-16 w-auto"
                />
                <div>
                  <h3 className="font-display font-bold text-2xl">Fairway Alliance</h3>
                  <p className="text-cream/70 text-xs tracking-widest uppercase">One Game. One Network. One Fairway.</p>
                </div>
              </div>
              <p className="text-cream/80 font-body leading-relaxed max-w-md">
                The centralized national network for minority golf organizations, leagues, 
                tournaments, courses, vendors, and sponsorship opportunities.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-display font-semibold text-lg mb-6 text-gold">Explore</h4>
              <ul className="space-y-3">
                <li><Link to="/explore" className="text-cream/80 hover:text-gold transition-colors">The Clubhouse</Link></li>
                <li><Link to="/course-map" className="text-cream/80 hover:text-gold transition-colors">The Local 18</Link></li>
                <li><Link to="/calendar" className="text-cream/80 hover:text-gold transition-colors">The Itinerary</Link></li>
                <li><Link to="/marketplace" className="text-cream/80 hover:text-gold transition-colors">The Pro Shop</Link></li>
              </ul>
            </div>

            {/* Community */}
            <div>
              <h4 className="font-display font-semibold text-lg mb-6 text-gold">Community</h4>
              <ul className="space-y-3">
                <li><Link to="/register" className="text-cream/80 hover:text-gold transition-colors">Join the Alliance</Link></li>
                <li><Link to="/travel-guides" className="text-cream/80 hover:text-gold transition-colors">Away Games</Link></li>
                <li><Link to="/pros" className="text-cream/80 hover:text-gold transition-colors">Directory</Link></li>
                <li><Link to="/hbcu" className="text-cream/80 hover:text-gold transition-colors">NextGen Junior Alliance</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-cream/60 text-sm">
              © {new Date().getFullYear()} Fairway Alliance. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-sm">
              <a href="https://fairwayalliance.org/privacy" className="text-cream/60 hover:text-gold transition-colors">Privacy Policy</a>
              <a href="https://fairwayalliance.org/terms" className="text-cream/60 hover:text-gold transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MainLayout;
